import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addDays, format } from 'date-fns';

const DEFAULT_COMPANY = {
  name: 'SMTLINK SOLUTIONS',
  address1: 'CALLE 3 NUMERO 107, COL SAN JOSE EL ALTO, C.P. 76147, QUERETARO, QUERETARO',
  address2: '',
  taxId: '',
  website: 'WWW.SMTLINKSS.COM',
  phone: '+1 262-497-7859 / +52 4461032521',
  email: 'sales@smtlinkss.com',
  contactName: 'Ing. Filiberto Severiano',
  contactEmail: 'Fseveriano@smtlinkss.com',
  contactPhone: '262-497-7859'
};
const ISSUER_ADDRESS_PROFILES = {
  mx: [
    'CALLE 3 NUMERO 107, COL SAN JOSE EL ALTO, C.P. 76147, QUERETARO, QUERETARO',
    ''
  ],
  us: [
    '55 Saint Marks Ln, Islip',
    'Zip code 11751, New York, USA'
  ]
};
const INVOICE_ISSUER_PROFILES = {
  mx: {
    name: 'SMTLINK SOLUTIONS',
    address1: 'Calle 3 Numero 107',
    address2: 'Col. San Jose el Alto, C.P. 76147',
    address3: 'Queretaro, Queretaro, Mexico',
    taxLabel: 'RFC',
    taxId: '',
    phone: '+1 262-497-7859 / +52 4461032521',
    email: 'sales@smtlinkss.com',
    website: 'www.smtlinkss.com'
  },
  us: {
    name: 'SMTLINK SOLUTIONS LLC',
    address1: '55 Saint Marks Lane',
    address2: 'Islip, New York, 11751',
    address3: '',
    taxLabel: 'EIN #',
    taxId: '41-4670872',
    phone: '+1 262 497 7859',
    email: 'sales@smtlinkss.com',
    website: 'www.smtlinkss.com'
  }
};

const PO_META_TAG = '__PO_META__';
const AUTO_VALIDITY_DAYS = 25;
const COLORS = {
  primary: [25, 118, 210],
  text: [0, 0, 0],
  muted: [100, 100, 100],
  border: [200, 200, 200],
  light: [240, 240, 240],
  white: [255, 255, 255]
};

const toNumber = (value, fallback = 0) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const safeText = (value, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text.length > 0 ? text : fallback;
};

const currencyLocale = (currency) => {
  return currency === 'MXN' ? 'es-MX' : 'en-US';
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat(currencyLocale(currency), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(toNumber(amount));
};

const formatDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '-';
  return format(date, 'M/d/yyyy');
};

const computeAutoValidityDate = (issueDateValue) => {
  const issueDate = issueDateValue ? new Date(issueDateValue) : new Date();
  if (Number.isNaN(issueDate.getTime())) return formatDate(new Date());
  return formatDate(addDays(issueDate, AUTO_VALIDITY_DAYS));
};

const getDocumentTitle = (type) => {
  if (type === 'purchase_order') return 'PURCHASE ORDER';
  if (type === 'quote') return 'QUOTE';
  return 'INVOICE';
};

const normalizeIssuerAddressProfile = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return (normalized === 'us' || normalized === 'usa' || normalized === 'united_states') ? 'us' : 'mx';
};

const normalizeFilename = (text) => {
  return safeText(text, 'DOCUMENT')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_');
};

const parsePOMetaFromNotes = (rawNotes) => {
  const notesText = (rawNotes || '').toString().replace(/\r\n/g, '\n');
  if (!notesText.startsWith(PO_META_TAG)) {
    return { description: notesText, meta: {} };
  }

  const firstBreak = notesText.indexOf('\n');
  const metaRaw = firstBreak === -1 ? notesText.slice(PO_META_TAG.length) : notesText.slice(PO_META_TAG.length, firstBreak);
  const description = firstBreak === -1 ? '' : notesText.slice(firstBreak + 1).replace(/\r\n/g, '\n');

  try {
    const meta = JSON.parse(metaRaw);
    return {
      description,
      meta: meta && typeof meta === 'object' ? meta : {}
    };
  } catch (e) {
    return { description: notesText, meta: {} };
  }
};

const computeTaxBreakdown = (docData, subtotal, total) => {
  const taxRaw = toNumber(docData.tax);
  const totalFallbackTax = Math.max(total - subtotal, 0);
  const taxAsPercentLooksValid =
    subtotal > 0 &&
    taxRaw > 0 &&
    taxRaw <= 100 &&
    Math.abs(subtotal * (1 + taxRaw / 100) - total) <= 0.1;

  if (taxAsPercentLooksValid) {
    const taxAmount = subtotal * (taxRaw / 100);
    return {
      label: `TAX (${taxRaw.toFixed(2).replace(/\.00$/, '')}%)`,
      amount: taxAmount
    };
  }

  if (taxRaw > 0 && taxRaw <= total) {
    const percent = subtotal > 0 ? (taxRaw / subtotal) * 100 : null;
    const label = Number.isFinite(percent)
      ? `TAX (${percent.toFixed(2).replace(/\.00$/, '')}%)`
      : 'TAX';
    return { label, amount: taxRaw };
  }

  const percent = subtotal > 0 ? (totalFallbackTax / subtotal) * 100 : null;
  const label = Number.isFinite(percent)
    ? `TAX (${percent.toFixed(2).replace(/\.00$/, '')}%)`
    : 'TAX';

  return { label, amount: totalFallbackTax };
};

const getDocumentItems = (docData) => {
  const rawItems = Array.isArray(docData.items) ? docData.items : [];

  if (rawItems.length > 0) {
    return rawItems.map((item, index) => {
      const quantity = Math.max(toNumber(item.quantity, 1), 0.01);
      const unitPrice = toNumber(
        item.unit_price ?? item.price ?? item.unitPrice ?? item.amount,
        0
      );
      const lineTotal = toNumber(item.total, quantity * unitPrice);
      const description = safeText(
        item.description || item.name || item.title,
        `Item ${index + 1}`
      );

      return { description, quantity, unitPrice, lineTotal };
    });
  }

  return [
    {
      description: safeText(
        docData.project_name || docData.notes || docData.description,
        'General services'
      ),
      quantity: 1,
      unitPrice: toNumber(docData.total),
      lineTotal: toNumber(docData.total)
    }
  ];
};

const ensureSpace = (doc, y, neededHeight, topMargin = 18, bottomMargin = 18) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + neededHeight > pageHeight - bottomMargin) {
    doc.addPage();
    return topMargin;
  }
  return y;
};

const drawWrappedBlock = (doc, text, x, startY, width, options = {}) => {
  const {
    lineHeight = 4,
    topMargin = 18,
    bottomMargin = 18,
    fallback = '-'
  } = options;

  const lines = doc.splitTextToSize(safeText(text, fallback), width);
  let y = startY;

  lines.forEach((line) => {
    y = ensureSpace(doc, y, lineHeight, topMargin, bottomMargin);
    doc.text(line, x, y);
    y += lineHeight;
  });

  return y;
};

const splitPdfLines = (value) => safeText(value, '')
  .split(/\r?\n/)
  .map((line) => line.trim())
  .filter(Boolean);

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => String(entry || '').trim())
    .filter(Boolean);
};

const drawPdfLines = (doc, lines, x, startY, lineHeight, options = {}) => {
  const { maxWidth = 80, fontSize = 10, fontStyle = 'normal', color = COLORS.text } = options;
  let y = startY;
  doc.setFontSize(fontSize);
  doc.setTextColor(...color);
  doc.setFont(undefined, fontStyle);

  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(String(line || ''), maxWidth);
    doc.text(wrapped, x, y);
    y += Math.max(1, wrapped.length) * lineHeight;
  });

  return y;
};

const drawPdfLabelValue = (doc, label, value, x, y, options = {}) => {
  const { labelWidth = 32, fontSize = 10, maxWidth = 70 } = options;
  doc.setFontSize(fontSize);
  doc.setTextColor(...COLORS.text);
  doc.setFont(undefined, 'bold');
  doc.text(`${label}:`, x, y);
  doc.setFont(undefined, 'normal');
  const wrapped = doc.splitTextToSize(safeText(value), maxWidth);
  doc.text(wrapped, x + labelWidth, y);
  return y + (Math.max(1, wrapped.length) * (fontSize >= 10 ? 4.8 : 4.2));
};

const generateInvoiceReferencePDF = (doc, docData) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 14;
  const right = pageWidth - 14;
  const contentWidth = right - left;
  const columnGap = 6;
  const halfWidth = (contentWidth - columnGap) / 2;
  const accent = [91, 155, 213];
  const accentDark = [18, 103, 179];
  const border = [17, 17, 17];
  const parsedNotes = parsePOMetaFromNotes(docData.notes || '');
  const invoiceMeta = parsedNotes.meta || {};
  const currency = safeText(docData.currency, 'USD').toUpperCase();
  const issueDate = formatDate(docData.issue_date);
  const dueDate = docData.due_date ? formatDate(docData.due_date) : issueDate;
  const issuerProfile = normalizeIssuerAddressProfile(invoiceMeta.issuer_address_profile);
  const company = {
    ...DEFAULT_COMPANY,
    ...(INVOICE_ISSUER_PROFILES[issuerProfile] || INVOICE_ISSUER_PROFILES.mx)
  };
  const totalVal = toNumber(docData.total);
  const subtotalVal = toNumber(docData.subtotal, totalVal);
  const taxInfo = computeTaxBreakdown(docData, subtotalVal, totalVal);
  const rawItems = Array.isArray(invoiceMeta.items) && invoiceMeta.items.length
    ? invoiceMeta.items
    : (Array.isArray(docData.items) && docData.items.length ? docData.items : []);
  const effectiveItems = rawItems.length
    ? rawItems.map((item, index) => {
      const quantity = Math.max(toNumber(item.quantity ?? item.qty, 1), 0.01);
      const unitPrice = toNumber(item.unit_price ?? item.unitPrice ?? item.amount, 0);
      const amount = toNumber(item.line_total ?? item.total, quantity * unitPrice);
      return {
        itemCode: safeText(item.item_code || item.itemCode || item.code, `Item ${index + 1}`),
        description: safeText(item.description || item.name || item.title, `Item ${index + 1}`),
        quantity,
        unitPrice,
        amount
      };
    })
    : [{
      itemCode: safeText(invoiceMeta.item_code, 'Item 1'),
      description: safeText(docData.project_name || parsedNotes.description || docData.notes, 'General services'),
      quantity: Math.max(toNumber(invoiceMeta.quantity ?? invoiceMeta.qty, 1), 0.01),
      unitPrice: subtotalVal > 0 ? subtotalVal : totalVal,
      amount: subtotalVal > 0 ? subtotalVal : totalVal
    }];
  const scopeLines = splitPdfLines(parsedNotes.description || docData.notes);
  const detailLines = splitPdfLines(invoiceMeta.detail_description || invoiceMeta.additional_description || '');
  const customerAddressLines = splitPdfLines(invoiceMeta.customer_fixed_address || docData.address);
  const billToLines = [
    safeText(docData.client_name),
    ...((invoiceMeta.customer_tax_id || invoiceMeta.tax_id || invoiceMeta.rfc || docData.customer_id)
      ? [`RFC (Tax ID): ${safeText(invoiceMeta.customer_tax_id || invoiceMeta.tax_id || invoiceMeta.rfc || docData.customer_id)}`]
      : []),
    ...customerAddressLines
  ];
  const shipFromLines = splitPdfLines(
    invoiceMeta.ship_from_address
      || invoiceMeta.ship_from_location
      || invoiceMeta.ship_from
      || invoiceMeta.pickup_location
      || invoiceMeta.origin_address
      || 'Pickup location to be confirmed.'
  );
  const complianceLines = [
    `HS Code: ${safeText(invoiceMeta.hs_code || invoiceMeta.hsCode, 'To be determined by importer / customs broker')}`,
    ...normalizeStringArray(invoiceMeta.classification_lines || invoiceMeta.classification_compliance || [])
  ];
  if (complianceLines.length === 1) {
    const complianceTextLines = splitPdfLines(invoiceMeta.classification_compliance_text || invoiceMeta.compliance_notes || '');
    complianceLines.push(...(complianceTextLines.length ? complianceTextLines : ['Additional compliance details not provided.']));
  }
  const referenceValue = safeText(
    invoiceMeta.sales_order_reference
      || invoiceMeta.reference
      || invoiceMeta.quote_reference
      || invoiceMeta.sales_order
      || invoiceMeta.customer_po
      || docData.project_name,
    '-'
  );
  const transactionType = safeText(invoiceMeta.transaction_type, 'Commercial Sale');
  const countryOfOrigin = safeText(invoiceMeta.country_of_origin || invoiceMeta.origin_country, '-');
  const exportReason = safeText(invoiceMeta.export_reason, transactionType);
  const parsedBalanceDue = parseFloat(invoiceMeta.balance_due);
  const balanceDue = Number.isFinite(parsedBalanceDue)
    ? parsedBalanceDue
    : (String(docData.status || '').trim().toLowerCase() === 'paid' ? 0 : totalVal);
  const taxRate = subtotalVal > 0 ? (taxInfo.amount / subtotalVal) * 100 : 0;

  doc.setFontSize(28);
  doc.setTextColor(...COLORS.primary);
  doc.setFont(undefined, 'bold');
  doc.text('SMTLINK', left, 18);
  doc.setTextColor(...COLORS.text);
  doc.setFontSize(16);
  doc.text('SOLUTIONS', left + 1, 25);

  let companyY = 36;
  companyY = drawPdfLines(doc, [
    safeText(company.name),
    ...[company.address1, company.address2, company.address3].filter(Boolean),
    ...(company.taxId ? [`${safeText(company.taxLabel, issuerProfile === 'us' ? 'EIN #' : 'RFC')}: ${company.taxId}`] : []),
    `Phone: ${safeText(company.phone)} / Mail: ${safeText(company.email)}`,
    safeText(company.website).replace(/^https?:\/\//i, '')
  ], left, companyY, 4.6, { maxWidth: 60, fontSize: 9.4 });

  doc.setTextColor(...COLORS.text);
  doc.setFontSize(28);
  doc.setFont(undefined, 'normal');
  doc.text('INVOICE', right, 18, { align: 'right' });

  let metaY = 30;
  metaY = drawPdfLabelValue(doc, 'Invoice #', safeText(docData.invoice_number), 122, metaY, { labelWidth: 32, maxWidth: 42, fontSize: 9.2 });
  metaY = drawPdfLabelValue(doc, 'Invoice Date', issueDate, 122, metaY, { labelWidth: 32, maxWidth: 42, fontSize: 9.2 });
  metaY = drawPdfLabelValue(doc, 'Invoice Due Date', dueDate, 122, metaY, { labelWidth: 36, maxWidth: 38, fontSize: 9.2 });
  metaY = drawPdfLabelValue(doc, 'Sales Order / Reference', referenceValue, 122, metaY, { labelWidth: 49, maxWidth: 30, fontSize: 9.2 });
  metaY = drawPdfLabelValue(doc, 'Currency', currency, 122, metaY, { labelWidth: 32, maxWidth: 42, fontSize: 9.2 });
  metaY = drawPdfLabelValue(doc, 'Transaction Type', transactionType, 122, metaY, { labelWidth: 37, maxWidth: 37, fontSize: 9.2 });

  let y = Math.max(companyY, metaY) + 6;
  doc.setFillColor(...accent);
  doc.rect(left, y, halfWidth, 8, 'F');
  doc.rect(left + halfWidth + columnGap, y, halfWidth, 8, 'F');
  doc.setTextColor(...COLORS.text);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Bill To:', left + 2, y + 5.5);
  doc.text('Ship From (Pickup Location):', left + halfWidth + columnGap + 2, y + 5.5);

  const billToBottom = drawPdfLines(doc, billToLines.length ? billToLines : ['-'], left + 2, y + 13, 5, {
    maxWidth: halfWidth - 4,
    fontSize: 10
  });
  let shipBottom = drawPdfLines(doc, shipFromLines, left + halfWidth + columnGap + 2, y + 13, 5, {
    maxWidth: halfWidth - 4,
    fontSize: 10
  });
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('SHIPPING DETAILS', left + halfWidth + columnGap + 2, shipBottom + 2);
  shipBottom += 8;
  shipBottom = drawPdfLabelValue(doc, 'Incoterm', safeText(invoiceMeta.incoterm || invoiceMeta.delivery_term, '-'), left + halfWidth + columnGap + 2, shipBottom, {
    labelWidth: 22,
    maxWidth: halfWidth - 28,
    fontSize: 10
  });
  shipBottom = drawPdfLabelValue(doc, 'Country of Origin', countryOfOrigin, left + halfWidth + columnGap + 2, shipBottom, {
    labelWidth: 34,
    maxWidth: halfWidth - 40,
    fontSize: 10
  });
  shipBottom = drawPdfLabelValue(doc, 'Export Reason', exportReason, left + halfWidth + columnGap + 2, shipBottom, {
    labelWidth: 28,
    maxWidth: halfWidth - 34,
    fontSize: 10
  });

  const itemRows = effectiveItems.map((item, index) => {
    const descriptionLines = [
      safeText(item.description),
      ...(index === 0 ? [...scopeLines, ...detailLines] : [])
    ].filter(Boolean);
    const proportionalTax = taxInfo.amount > 0 && subtotalVal > 0 ? (item.amount / subtotalVal) * taxInfo.amount : 0;
    return [
      safeText(item.itemCode, `Item ${index + 1}`),
      descriptionLines.join('\n'),
      Number.isInteger(item.quantity) ? String(item.quantity) : item.quantity.toFixed(2),
      formatCurrency(item.unitPrice, currency),
      formatCurrency(item.amount, currency),
      formatCurrency(proportionalTax, currency)
    ];
  });

  autoTable(doc, {
    startY: Math.max(billToBottom, shipBottom) + 8,
    head: [['Item Number', 'Item Description', 'Qty', 'Unit Price', 'Extended', 'Tax']],
    body: itemRows,
    theme: 'grid',
    margin: { left, right: pageWidth - right },
    headStyles: {
      fillColor: accent,
      textColor: COLORS.text,
      fontStyle: 'bold',
      lineColor: border,
      lineWidth: 0.2
    },
    styles: {
      fontSize: 8.8,
      cellPadding: 2.2,
      lineColor: border,
      lineWidth: 0.2,
      textColor: COLORS.text,
      valign: 'top'
    },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 80 },
      2: { cellWidth: 14, halign: 'center' },
      3: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
      4: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
      5: { cellWidth: 20, halign: 'right' }
    }
  });

  y = (doc.lastAutoTable?.finalY || 120) + 4;
  if (y + 44 > pageHeight - 28) {
    doc.addPage();
    y = 18;
  }

  const complianceWidth = 100;
  const totalsWidth = contentWidth - complianceWidth;
  const totalsLabelWidth = 41;
  const totalsRows = [
    { label: `Tax Total (${taxRate.toFixed(4)})`, value: formatCurrency(taxInfo.amount, currency) },
    { label: 'Sub Total:', value: formatCurrency(subtotalVal, currency) },
    { label: 'Grand Total:', value: formatCurrency(totalVal, currency), bold: true },
    { label: 'Balance Due:', value: formatCurrency(balanceDue, currency) }
  ];
  const complianceText = complianceLines.map((line) => `- ${String(line || '').replace(/^-+\s*/, '')}`);
  const complianceHeight = Math.max(28, complianceText.length * 5 + 10);
  const totalsHeight = totalsRows.length * 9;
  const blockHeight = Math.max(complianceHeight, totalsHeight);

  doc.setDrawColor(...border);
  doc.rect(left, y, complianceWidth, blockHeight);
  doc.setFontSize(9.4);
  doc.setTextColor(...COLORS.text);
  doc.setFont(undefined, 'bold');
  doc.text('CLASSIFICATION & COMPLIANCE', left + 2, y + 5.5);
  drawPdfLines(doc, complianceText, left + 2, y + 11, 4.6, {
    maxWidth: complianceWidth - 4,
    fontSize: 8.4,
    fontStyle: 'bold'
  });

  let totalsY = y;
  totalsRows.forEach((row) => {
    doc.setFillColor(...accent);
    doc.rect(left + complianceWidth, totalsY, totalsLabelWidth, 9, 'F');
    doc.rect(left + complianceWidth + totalsLabelWidth, totalsY, totalsWidth - totalsLabelWidth, 9);
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.text);
    doc.setFont(undefined, row.bold ? 'bold' : 'normal');
    doc.text(row.label, left + complianceWidth + 2, totalsY + 5.8);
    doc.text(row.value, right - 2, totalsY + 5.8, { align: 'right', maxWidth: totalsWidth - totalsLabelWidth - 3 });
    totalsY += 9;
  });

  y += blockHeight + 8;
  if (y + 18 > pageHeight - 12) {
    doc.addPage();
    y = 18;
  }

  doc.setTextColor(...accentDark);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('Declaration:', left, y);
  doc.text('I hereby certify that the information contained in this invoice is true and correct.', left, y + 8, {
    maxWidth: contentWidth
  });

  const normalizedNumber = normalizeFilename(docData.invoice_number);
  doc.save(`INVOICE_${normalizedNumber}.pdf`);
};

export const generatePurchaseOrderPDF = (docData) => {
  const doc = new jsPDF({
    unit: 'mm',
    format: docData.type === 'invoice' ? 'letter' : 'a4'
  });
  if (docData.type === 'invoice') {
    generateInvoiceReferencePDF(doc, docData);
    return;
  }
  const pageWidth = doc.internal.pageSize.getWidth();
  const parsedNotes = parsePOMetaFromNotes(docData.notes || '');
  const poMeta = parsedNotes.meta || {};
  const descriptionText = safeText(
    docData.description || parsedNotes.description || docData.notes,
    'No additional scope provided.'
  );

  const title = getDocumentTitle(docData.type);
  const currency = safeText(docData.currency, 'USD').toUpperCase();
  const issuerProfile = normalizeIssuerAddressProfile(poMeta.issuer_address_profile);
  const profileAddressLines = ISSUER_ADDRESS_PROFILES[issuerProfile] || ISSUER_ADDRESS_PROFILES.mx;
  const incomingCompany = docData.company || {};
  const company = {
    ...DEFAULT_COMPANY,
    ...incomingCompany,
    address1: safeText(incomingCompany.address1, profileAddressLines[0] || DEFAULT_COMPANY.address1),
    address2: safeText(incomingCompany.address2, profileAddressLines[1] || '')
  };

  const totalVal = toNumber(docData.total);
  const subtotalVal = toNumber(docData.subtotal, totalVal);
  const taxInfo = computeTaxBreakdown(docData, subtotalVal, totalVal);
  const docItems = getDocumentItems({ ...docData, notes: descriptionText });

  // Header
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.primary);
  doc.setFont(undefined, 'bold');
  doc.text(safeText(company.name), 14, 18);

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.muted);
  doc.setFont(undefined, 'normal');
  doc.text(safeText(company.address1, ''), 14, 23);
  doc.text(safeText(company.address2, ''), 14, 27);
  doc.text(`WEBSITE: ${safeText(company.website, '-')}`, 14, 31);
  doc.text(`PHONE: ${safeText(company.phone, '-')}`, 14, 35);

  doc.setFontSize(22);
  doc.setTextColor(...COLORS.primary);
  doc.setFont(undefined, 'bold');
  doc.text(title, pageWidth - 14, 18, { align: 'right' });

  // Meta table
  const metaData = [
    ['DATE', formatDate(docData.issue_date)],
    [`${title} #`, safeText(docData.invoice_number)],
    ...(docData.type === 'purchase_order' ? [] : [
      ['CUSTOMER ID', safeText(docData.client_id, safeText(docData.client_name, '-'))]
    ]),
    ['VALID UNTIL', docData.due_date ? formatDate(docData.due_date) : computeAutoValidityDate(docData.issue_date)]
  ];

  autoTable(doc, {
    startY: 23,
    margin: { left: pageWidth - 80, right: 14 },
    body: metaData,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 1.6,
      lineColor: COLORS.border,
      lineWidth: 0.1,
      textColor: COLORS.text
    },
    columnStyles: {
      0: { fillColor: COLORS.light, fontStyle: 'bold', cellWidth: 28 },
      1: { halign: 'center' }
    }
  });

  // Recipient block: purchase orders are addressed to the supplier, not the end customer.
  let currentY = 54;

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.setFont(undefined, 'bold');
  doc.text(docData.type === 'purchase_order' ? 'PURCHASE ORDER TO' : 'CUSTOMER', 14, currentY);

  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.setFont(undefined, 'normal');
  const customerAddressLines = safeText(poMeta.customer_fixed_address || docData.address, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const customerLines = docData.type === 'purchase_order'
    ? [
        safeText(poMeta.supplier_name, '-'),
        ...(poMeta.supplier_contact ? [`Supplier Contact: ${poMeta.supplier_contact}`] : []),
        ...(poMeta.supplier_email ? [`Supplier Email: ${poMeta.supplier_email}`] : []),
        ...(poMeta.supplier_phone ? [`Supplier Phone: ${poMeta.supplier_phone}`] : []),
        ...safeText(poMeta.supplier_address, '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
      ]
    : [
        `Contact: ${safeText(docData.client_contact, '')}`,
        `Company: ${safeText(docData.client_name)}`,
        `Mail: ${safeText(poMeta.contact_email || docData.contact_email || docData.client_email, '-')}`,
        ...(customerAddressLines.length
          ? customerAddressLines.map((line, index) => (index === 0 ? `Address: ${line}` : line))
          : ['Address: -']),
        `Phone: ${safeText(docData.phone, '-')}`
      ];
  const customerStartY = currentY + 5;
  customerLines.forEach((line, index) => {
    doc.text(line, 14, customerStartY + (index * 5));
  });
  const customerBlockBottomY = customerStartY + ((customerLines.length - 1) * 5);

  // Commercial terms table
  autoTable(doc, {
    startY: customerBlockBottomY + 5,
    head: [['Lead Time', 'Payment Term', 'Incoterm / Delivery']],
    body: [[
      safeText(docData.lead_time || poMeta.lead_time, 'When needed'),
      safeText(docData.payment_terms || poMeta.payment_terms, '30 Days'),
      safeText(docData.delivery_term || docData.ddp || poMeta.delivery_term, 'QRO')
    ]],
    theme: 'grid',
    tableWidth: 120,
    margin: { left: 14 },
    headStyles: {
      fillColor: COLORS.light,
      textColor: COLORS.text,
      fontStyle: 'bold',
      lineColor: COLORS.border,
      lineWidth: 0.1
    },
    styles: {
      fontSize: 8,
      halign: 'center',
      cellPadding: 2,
      lineColor: COLORS.border,
      lineWidth: 0.1
    }
  });

  // Description
  currentY = (doc.lastAutoTable?.finalY || currentY + 25) + 8;
  currentY = ensureSpace(doc, currentY, 18);

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.setFont(undefined, 'bold');
  doc.text('DESCRIPTION', 14, currentY);

  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.text(
    safeText(docData.project_name, 'Services / Products Description'),
    14,
    currentY + 5
  );

  doc.setFont(undefined, 'bold');
  doc.text('Scope:', 14, currentY + 10);

  doc.setFont(undefined, 'normal');
  currentY = drawWrappedBlock(
    doc,
    descriptionText,
    14,
    currentY + 14,
    pageWidth - 28,
    { lineHeight: 4, fallback: 'No additional scope provided.' }
  );

  // Items
  const itemsBody = docItems.map((item) => [
    item.description,
    item.quantity.toFixed(2).replace(/\.00$/, ''),
    formatCurrency(item.unitPrice, currency),
    formatCurrency(item.lineTotal, currency)
  ]);

  const itemTableStartY = ensureSpace(doc, currentY + 6, 24);

  autoTable(doc, {
    startY: itemTableStartY,
    head: [['DESCRIPTION', 'QTY', `UNIT (${currency})`, `TOTAL (${currency})`]],
    body: itemsBody,
    theme: 'striped',
    margin: { left: 14, right: 14 },
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 8.5,
      cellPadding: 2.5,
      textColor: COLORS.text
    },
    columnStyles: {
      0: { cellWidth: 92 },
      1: { cellWidth: 18, halign: 'right' },
      2: { cellWidth: 36, halign: 'right' },
      3: { cellWidth: 36, halign: 'right', fontStyle: 'bold' }
    }
  });

  // Totals
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || itemTableStartY) + 2,
    body: [
      [`SUBTOTAL (${currency})`, formatCurrency(subtotalVal, currency)],
      [taxInfo.label, formatCurrency(taxInfo.amount, currency)],
      [`TOTAL (${currency})`, formatCurrency(totalVal, currency)]
    ],
    theme: 'plain',
    margin: { left: pageWidth - 90, right: 14 },
    styles: {
      fontSize: 9,
      halign: 'right',
      cellPadding: 1.6
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 46 },
      1: { cellWidth: 30, fontStyle: 'bold' }
    }
  });

  // Footer terms and signature
  currentY = (doc.lastAutoTable?.finalY || itemTableStartY) + 8;
  currentY = ensureSpace(doc, currentY, 30);

  doc.setFontSize(9);
  doc.setTextColor(...COLORS.primary);
  doc.setFont(undefined, 'bold');
  doc.text('TERMS AND CONDITIONS', 14, currentY);

  const defaultTerms = [
    `Currency: All prices are expressed in ${currency}.`,
    'Payment terms: Payment must be made within the agreed terms specified in this document.',
    'Lead time: Delivery starts once order confirmation is received.',
    'Order confirmation: The purchase order must be formally sent to the designated email address.',
    'Cancellations: Due to material sourcing, confirmed orders are non-cancellable.'
  ];

  const termsFromMeta = Array.isArray(poMeta.terms) ? poMeta.terms : [];
  const terms = Array.isArray(docData.terms) && docData.terms.length > 0
    ? docData.terms
    : (termsFromMeta.length > 0 ? termsFromMeta : defaultTerms);

  doc.setFontSize(7.3);
  doc.setTextColor(...COLORS.text);
  doc.setFont(undefined, 'normal');

  let termsY = currentY + 5;
  terms.forEach((term, index) => {
    termsY = drawWrappedBlock(
      doc,
      `${index + 1}. ${term}`,
      14,
      termsY,
      pageWidth - 28,
      { lineHeight: 3.9 }
    );
  });

  termsY = ensureSpace(doc, termsY + 5, 22);

  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.text);
  doc.setFont(undefined, 'bold');
  doc.text('Customer Acceptance (sign below):', 14, termsY);

  doc.setLineWidth(0.4);
  doc.line(14, termsY + 10, 90, termsY + 10);
  doc.setFont(undefined, 'normal');
  doc.text('Print Name:', 14, termsY + 15);

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.muted);
  doc.text('If you have any questions about this document, please contact:', 14, termsY + 24);
  doc.text(
    `${safeText(company.contactName, 'Sales Team')}, Phone: ${safeText(company.contactPhone, '-')}, ${safeText(company.contactEmail, '-')}`,
    14,
    termsY + 28
  );

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.setFont(undefined, 'bold');
  doc.text('Thank You For Your Business!', 14, termsY + 36);

  const normalizedTitle = title.replace(/\s+/g, '_');
  const normalizedNumber = normalizeFilename(docData.invoice_number);
  doc.save(`${normalizedTitle}_${normalizedNumber}.pdf`);
};

export const generatePDF = (docData) => {
  generatePurchaseOrderPDF(docData);
};
